import { Minus, Plus, Trash, Warning, Star, Wallet } from "@phosphor-icons/react";

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
const TYPE = { dine_in: "Dine in", takeaway: "Takeaway" };

export function KioskOrderPanel({ session, onAction, onUseCredit, locked, onOptions, menu }) {
  const s = session;
  const cr = s.credit || {};
  return (
    <div className="flex flex-col h-full bg-white rounded-[28px] shadow-[0_30px_80px_-30px_rgba(10,30,45,0.45)] overflow-hidden" data-testid="kiosk-order-panel">
      <div className="px-7 pt-7 pb-4 border-b border-[rgba(15,42,61,0.08)]">
        <div className="text-xs uppercase tracking-[0.2em] text-[#5c7385]" data-testid="kiosk-order-title">In your order</div>
        <div className="flex flex-wrap gap-2 mt-3">
          {s.order_type && <Chip testid="kiosk-chip-type">{TYPE[s.order_type]}{s.table_number ? ` — Table ${s.table_number}` : ""}</Chip>}
          {s.customer_name && <Chip testid="kiosk-chip-name">Customer: {s.customer_name.split(" ")[0]}</Chip>}
          {s.flow?.masked_phone && <Chip testid="kiosk-chip-phone">Mobile {s.flow.masked_phone}</Chip>}
          {s.flow?.pending && <Chip testid="kiosk-chip-pending" tone="pending">{s.flow.pending.display} · waiting for your YES</Chip>}
          {s.loyalty?.status === "linked" && <Chip testid="kiosk-chip-loyalty" tone="gold"><Star size={14} weight="fill" /> {s.loyalty.display_name} · {s.loyalty.points} pts</Chip>}
          {s.payment && <Chip testid="kiosk-chip-payment">{s.payment === "cash" ? "Cash · pay at counter" : s.payment === "online" ? "Online card" : "Card · EFTPOS"}</Chip>}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3" data-testid="kiosk-cart-lines">
        {!s.cart.length && <div className="h-full flex items-center justify-center text-center text-[#7d8f9c] px-8 text-lg">Your order will appear here once you confirm it.</div>}
        {s.cart.map((ln, i) => (
          <div key={`${ln.menu_item_id}-${i}`} className={`kiosk-line flex items-start gap-3 px-3 py-3.5 rounded-2xl ${ln.flags?.length ? "bg-[#fff6e5]" : ""}`} style={{ animationDelay: `${i * 40}ms` }} data-testid={`kiosk-line-${i}`}>
            <div className="flex-1 min-w-0">
              <div className="text-lg font-semibold text-[#0f2a3d] font-heading">{ln.qty} × {ln.name}</div>
              {ln.modifiers.length > 0 && <div className="text-sm text-[#5c7385]">{ln.modifiers.map((m) => m.name).join(" · ")}</div>}
              {ln.notes && <div className="text-sm text-[#5c7385] italic">{ln.notes}</div>}
              {ln.flags?.length > 0 && <div className="text-sm text-[#a15c00] flex items-center gap-1 mt-1" data-testid={`kiosk-line-flag-${i}`}><Warning size={14} />{ln.flags.join(" · ")}</div>}
            </div>
            <div className="text-right">
              <div className="font-semibold text-[#0f2a3d] tabular-nums">{fmt(ln.line_total)}</div>
              {!locked && (
                <div className="flex items-center gap-1 mt-2">
                  <IconBtn testid={`kiosk-line-minus-${i}`} onClick={() => onAction({ type: "update_line", line: i + 1, qty: ln.qty - 1 })}>{ln.qty > 1 ? <Minus size={16} /> : <Trash size={16} />}</IconBtn>
                  <IconBtn testid={`kiosk-line-plus-${i}`} onClick={() => onAction({ type: "update_line", line: i + 1, qty: ln.qty + 1 })}><Plus size={16} /></IconBtn>
                  {onOptions && menu?.find((m) => m.id === ln.menu_item_id)?.groups?.length > 0 && <button type="button" data-testid={`kiosk-line-options-${i}`} onClick={() => onOptions(i)} className="h-10 px-3 rounded-full bg-[#eef2f5] text-[#0f2a3d] text-sm font-semibold">Options / Modifiers</button>}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {cr.status === "approved" && !locked && <CreditBox cr={cr} total={s.cart_total} onUseCredit={onUseCredit} />}
      {cr.status === "pending" && <div className="mx-5 mb-3 px-4 py-3 rounded-2xl bg-[#eaf3f8] text-[#0f2a3d] text-sm flex items-center gap-2" data-testid="kiosk-credit-pending"><Wallet size={18} /> A staff member is verifying your account — this only takes a moment.</div>}
      <div className="px-7 py-5 bg-[#f4f1ea] space-y-1">
        <Row label="Order total" value={fmt(s.cart_total)} testid="kiosk-cart-total" />
        {cr.apply_amount > 0 && <Row label="Account credit" value={`− ${fmt(cr.apply_amount)}`} testid="kiosk-credit-applied" />}
        <Row big label="To pay" value={fmt(s.estimated_due)} testid="kiosk-estimated-due" />
      </div>
    </div>
  );
}

function CreditBox({ cr, total, onUseCredit }) {
  const max = Math.min(cr.balance || 0, total);
  return (
    <div className="mx-5 mb-3 px-4 py-3 rounded-2xl bg-[#e9f6ee] text-[#124a2c]" data-testid="kiosk-credit-approved">
      <div className="text-sm font-medium">Verified · account credit available: {fmt(cr.balance)}</div>
      <div className="flex gap-2 mt-2">
        <button className="kiosk-pill bg-[#124a2c] text-white" data-testid="kiosk-credit-use-max" disabled={max <= 0} onClick={() => onUseCredit("max")}>Use {fmt(max)}</button>
        <button className="kiosk-pill bg-white text-[#124a2c]" data-testid="kiosk-credit-none" onClick={() => onUseCredit(0)}>Don't use credit</button>
      </div>
    </div>
  );
}

const Row = ({ label, value, big, testid }) => (
  <div className={`flex justify-between ${big ? "text-2xl font-bold text-[#0f2a3d] font-heading pt-1" : "text-sm text-[#5c7385]"}`}>
    <span>{label}</span><span className="tabular-nums" data-testid={testid}>{value}</span>
  </div>
);

const Chip = ({ children, tone, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${tone === "gold" ? "bg-[#fff2cc] text-[#7a5600]" : tone === "pending" ? "bg-white border-2 border-dashed border-[#e2572b] text-[#a33d1a]" : "bg-[#eaf0f4] text-[#0f2a3d]"}`}>{children}</span>
);

const IconBtn = ({ children, onClick, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick} className="h-10 w-10 rounded-full bg-[#eef2f5] text-[#0f2a3d] inline-flex items-center justify-center active:scale-90 transition-transform">{children}</button>
);
